/**
 * Edit a user's basic info + app role. Verified status is informational
 * only (never togglable from this UI, matching the original behavior) —
 * shown as a badge, or the activation link when still unverified.
 *
 * @param {Object=} user - null/no uuid closes the dialog
 * @param {Object} users - users store state (for update/updateError)
 * @param {function} onEdit - (uuid, formData) => void
 * @param {function} onClose
 */
import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../theme/untitled/Modal/Modal";
import Button from "../../theme/untitled/Button/Button";
import Input from "../../theme/untitled/Input/Input";
import Select from "../../theme/untitled/Select/Select";
import Badge from "../../theme/untitled/Badge/Badge";
import Icon from "../../theme/untitled/icons/Icon";
import ApiError from "../../component/ApiError";
import { AppUserRole, AppUserRoleOptions } from "../../consts/users";
import styles from "./UsersPage.module.scss";

const EditModal = ({ user, users, onEdit, onClose }) => {
  const open = !!user?.uuid;

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: AppUserRole.user,
  });

  useEffect(() => {
    if (user?.uuid) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        role: user.role,
      });
    }
  }, [user]);

  // Hooks must run unconditionally on every render, so these are defined
  // before the `!open` early return below — harmless while `user` is null,
  // since the callbacks are only ever invoked once the modal (and `user`)
  // is actually open.
  const onFirstNameChange = useCallback((e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, first_name: value }));
  }, []);

  const onLastNameChange = useCallback((e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, last_name: value }));
  }, []);

  const onEmailFieldChange = useCallback((e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, email: value }));
  }, []);

  const onRoleChange = useCallback((e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, role: value }));
  }, []);

  const onSave = useCallback(
    (e) => {
      e.preventDefault();
      onEdit(user.uuid, { ...form, is_account_verified: user.is_account_verified });
    },
    [onEdit, user, form]
  );

  if (!open) {
    return null;
  }

  const { update, updateError } = users;
  const isVerified = user.is_account_verified * 1 === 1;

  const orgUUID = user.organizations ? user.organizations[0]?.uuid : "";
  const activationUrl = !isVerified
    ? `${window.location.origin}/users/verify_account/${encodeURIComponent(user.email)}/${user.activation_token}/${orgUUID}`
    : null;

  const verifiedStatus = isVerified ? (
    <Badge color="success" size="sm">
      Email verified
    </Badge>
  ) : (
    <div className={styles.dialogNote}>
      <Icon name="AlertCircle" size={16} className={styles.dialogNoteIcon} />
      <span>
        Email not verified. Activation link:{" "}
        <a href={activationUrl} target="_blank" rel="noreferrer">
          {activationUrl}
        </a>
      </span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={update ? undefined : onClose}
      size="lg"
      title="Edit user"
      footer={
        <>
          <Button hierarchy="secondaryGray" onClick={onClose} disabled={update}>
            Cancel
          </Button>
          <Button hierarchy="primary" type="submit" form="edit-user-form" disabled={update} loading={update}>
            {update ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={onSave}>
        <div className={styles.dialogGrid}>
          <Input
            size="sm"
            label="First name"
            value={form.first_name}
            onChange={onFirstNameChange}
            disabled={update}
            maxLength={64}
            minLength={2}
          />
          <Input
            size="sm"
            label="Last name"
            value={form.last_name}
            onChange={onLastNameChange}
            disabled={update}
            maxLength={64}
            minLength={2}
          />
        </div>

        <div className={styles.dialogField}>
          <Input
            size="sm"
            label="Email"
            type="text"
            value={form.email}
            onChange={onEmailFieldChange}
            disabled={update}
          />
        </div>

        <div className={styles.dialogField}>
          <label htmlFor="edit-user-role">App role</label>
          <Select
            id="edit-user-role"
            size="sm"
            value={form.role}
            onChange={onRoleChange}
            options={AppUserRoleOptions}
            disabled={update}
          />
        </div>

        <div className={styles.dialogField}>{verifiedStatus}</div>

        <ApiError error={updateError} defaultErrorMessage="Could not update user data" />
      </form>
    </Modal>
  );
};

export default EditModal;
